// fixedPointCollector.js — Shared factory helper for bespoke N-point draw tools.
//
// Creates a minimal OL-compatible "interaction" handle that:
//   1. Listens for a single left-click on the map
//   2. Generates all `targetCount` points spread in a radial pattern around the click
//   3. Immediately calls onFinish(spreadCoords) and removes itself
//
// Returns { getPlacedCount(), destroy() } — the shape expected by useMapEditor.
//
// This collector is used exclusively for SYMMETRIC fixed-count shapes (no meaningful
// tip/rear or single-click-vs-target directionality — see drawRuleMeta.js) — directional
// arrow rules use directionalArrowTools.js's sequential click-by-click collector instead,
// which is what actually gets a working live mil-sym preview (see that file's comment).
//
// A previous version of this factory accepted `sidc`/`minPreviewPoints` and wired
// startLivePreview() to it, but that wiring was provably dead: `onClick` generates all
// `targetCount` points and calls `finish()` synchronously on the FIRST click, so
// `collected.length` jumps straight from 0 to `targetCount` — there is never an
// intermediate frame where `minPreviewPoints <= collected.length < targetCount` for
// startLivePreview's pointermove handler to render against. Removed rather than "fixed" —
// making it real for a genuinely single-click gesture would require rendering a preview
// against the *spread* coordinates before the click even happens (i.e. redesigning what
// "preview" means here), which isn't worth it for shapes where placement position, not
// orientation, is the only thing the user is actually choosing.
//
// Ticket 04: NOT rebuilt on multiPointCollector.js despite being in that ticket's file list —
// there is no sequential click/pointermove/keydown loop here to consolidate. The single click
// handler generates every point and finishes atomically in the same tick, so there's no
// intermediate "N of M points placed" state for Escape to cancel out of or Backspace to undo a
// step within — by the time either keystroke could fire, the collector has already finished
// and torn itself down. Both conventions remain meaningful (and applicable) only for the other
// files in that list, which really do collect points one click at a time.

import { toLonLat } from 'ol/proj';
import { generateSpread } from './geometryUtils';

export function createFixedPointCollector(map, {
    targetCount,
    onFinish,
    customSketch = null,     // kept for API compatibility — no-op in single-click path
    allowEarlyFinish = false, // kept for API compatibility — no-op
    minEarlyCount = null,     // kept for API compatibility — no-op
}) {
    let collected = [];    // [[lng,lat], ...] in EPSG:4326

    function finish() {
        cleanup();
        onFinish([...collected]);
    }

    function cleanup() {
        map.un('click', onClick);
    }

    const onClick = (e) => {
        if (e.originalEvent.button !== 0) return;
        const pts3857 = generateSpread(e.coordinate, targetCount, map);
        collected = pts3857.map((c) => toLonLat(c));
        finish();
    };

    map.on('click', onClick);

    return {
        interaction: null, // no ol/interaction used directly
        getPlacedCount: () => collected.length,
        destroy: cleanup,
    };
}
